import { Button, Filter, Text, VSpacer } from "@/components/DesignSystem";
import { QueryKeys } from "@/constants/QueryKeys";
import { useCanWrite } from '@/hooks/useCanWrite';
import { useUserList } from '@/hooks/useUserList';
import { UserEndpoint } from '@api/endpoints';
import { ApiUser } from "@api/interfaces/ApiUser";
import AddIcon from '@mui/icons-material/Add';
import { Pagination, Stack } from "@mui/material";
import { UserType } from "@shared/enums";
import React, { Fragment, useState } from "react";
import { useQueryClient } from "react-query";
import AdminListItem from "./AdminListItem";
import CreateAdminModal from "./CreateAdminModal";

/** The internal admins, with their filter and Create. */
const AdminsTab = () => {
  const canWrite = useCanWrite();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const queryClient = useQueryClient();
  const [selectedFilters, setSelectedFilters] = (
    useState<UserEndpoint.List.Query>({})
  );

  const { users } = useUserList({ ...selectedFilters, userType: [UserType.Internal] });

  return (
    <Stack>
      <Stack alignItems="center" direction="row" justifyContent="space-between">
        <Text category="h3">
          Admin Users
        </Text>
        <Button
          disabled={!canWrite}
          onClick={() => setShowCreateModal(true)}
          size="giant"
          startIcon={<AddIcon />}
          testID="admin-list-create-button"
        >
          Create
        </Button>
      </Stack>
      <VSpacer size="9" />
      <Stack alignItems="center" direction="row" justifyContent="space-between">
        <Text category="p2">{users?.total ?? 0} results</Text>
      </Stack>
      <VSpacer size="5" />
      <Filter<UserEndpoint.List.Query>
        filters={[
          {
            id: 'daysCreated',
            label: 'Sign-up date',
            options: [
              { id: 1, label: 'Past day' },
              { id: 2, label: 'Past 2 days' },
              { id: 7, label: 'Past week' },
              { id: 30, label: 'Past month' },
            ],
            singleSelect: true,
          },
        ]}
        onChange={setSelectedFilters}
        recordName={{
          plural: "Admins",
          singular: "Admin",
        }}
        testID="admin-list-filter"
        totalResults={users?.total}
      />
      {users?.data.map((user) => (
        <Fragment key={user.id}>
          <AdminListItem admin={user as ApiUser}/>
          <VSpacer size="3" />
        </Fragment>
      ))}
      {users && users.lastPage !== 0 &&
        <Stack alignItems="center">
          <VSpacer size="9" />
          <Pagination
            count={users.lastPage + 1}
            onChange={
              (event, page) => setSelectedFilters({ ...selectedFilters, page: page - 1 })
            }
            page={users.page + 1}
          />
        </Stack>
      }
      {showCreateModal && (
        <CreateAdminModal
          onClose={() => {
            setShowCreateModal(false);
            void queryClient.invalidateQueries(QueryKeys.LIST_USERS);
          }}
        />
      )}
    </Stack>
  );
};

export default AdminsTab;
